// apis/api/DealerStatistic.ts
import envStore from '@utils/env';
import BaseApi, { GetHeader } from '../BaseApi';
import { IDealerStatisticRow, IFilterDealerStatisticRequest } from '@pages/Report/ThongKeHetPhi/models/DealerStatistic';

interface IDealerStatisticApi {
  // Danh mục
  GetListRegion: () => Promise<any>;
  GetListServer: () => Promise<any>;
  GetListDeviceType: () => Promise<any>;

  // Danh sách kỳ / chi tiết kỳ
  GetDealerImport: () => Promise<any>;
  GetDataByPeriodId: (importPeriodId: number) => Promise<any>;

  // Sửa 1 dòng
  UpdateDealerStatistic: (dataBody: Partial<IDealerStatisticRow>, id: any) => Promise<any>;

  // Lọc / tìm đại lý
  FilterDealerStatistic: (dataBody: IFilterDealerStatisticRequest) => Promise<any>;
  SearchDealer: (dataBody: { importPeriodId: number; keyword: string }) => Promise<any>;

  // Import: gửi file Excel thô, BE tự parse + tự map Region/Server/DeviceType
  ImportDealerStatisticExcel: (year: number, month: number, file: File) => Promise<any>;

  // Export: trả file theo kỳ (BE có thể trả .xlsx hoặc .zip nhiều file/đại lý)
  ExportDealerStatistic: (importPeriodId: number) => Promise<{ blob: Blob; fileName?: string; contentType: string }>;
}

const DealerStatisticApi: IDealerStatisticApi = {
  GetListRegion: () => {
    return BaseApi.GET('api/DealerStatistic/GetListRegion', envStore.url_auth);
  },
  GetListServer: () => {
    return BaseApi.GET('api/DealerStatistic/GetListServer', envStore.url_auth);
  },
  GetListDeviceType: () => {
    return BaseApi.GET('api/DealerStatistic/GetListDeviceType', envStore.url_auth);
  },

  GetDealerImport: () => {
    return BaseApi.GET('api/DealerStatistic/GetDealerImport', envStore.url_auth);
  },

  GetDataByPeriodId: (importPeriodId: number) => {
    return BaseApi.GET(`api/DealerStatistic/GetDataByPeriodId?ImportPeriodId=${importPeriodId}`, envStore.url_auth);
  },

  UpdateDealerStatistic: (dataBody: Partial<IDealerStatisticRow>, id: any) => {
    return BaseApi.PUT(`api/DealerStatistic/UpdateDealerStatistic?Id=${id}`, dataBody, envStore.url_auth);
  },

  FilterDealerStatistic: (dataBody: IFilterDealerStatisticRequest) => {
    return BaseApi.POST('api/DealerStatistic/FilterDealerStatistic', dataBody, envStore.url_auth);
  },

  SearchDealer: (dataBody: { importPeriodId: number; keyword: string }) => {
    return BaseApi.POST('api/DealerStatistic/SearchDealer', dataBody, envStore.url_auth);
  },

  ImportDealerStatisticExcel: (year: number, month: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return BaseApi.POST(
      `api/DealerStatistic/ImportDealerStatisticExcel?year=${year}&month=${month}`,
      formData,
      envStore.url_auth
    );
  },

  // BaseApi.GET không hỗ trợ responseType blob nên gọi fetch trực tiếp (giống cách
  // tải file ở Reflect.tsx) để không bị hỏng dữ liệu nhị phân khi parse response.
  ExportDealerStatistic: async (importPeriodId: number) => {
    const res = await fetch(
      `${envStore.url_auth}api/DealerStatistic/ExportDealerStatistic?importPeriodId=${importPeriodId}`,
      { method: 'GET', headers: GetHeader() }
    );

    const contentType = res.headers.get('content-type') || '';

    // BE trả lỗi dạng JSON (kể cả status 200) thay vì file thật -> đọc trước để
    // không lưu nhầm nội dung lỗi thành file .xlsx/.zip không mở được.
    if (contentType.includes('application/json')) {
      const errJson = await res.json().catch(() => null);
      throw new Error(errJson?.message || `Xuất file thất bại (status ${res.status}).`);
    }

    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }

    const disposition = res.headers.get('content-disposition') || '';
    const fileNameMatch = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    const fileName = fileNameMatch ? decodeURIComponent(fileNameMatch[1]) : undefined;

    const blob = await res.blob();
    return { blob, fileName, contentType };
  }
};

export default DealerStatisticApi;
